/**
 * Seeded and unseeded randomness for the research-design tools.
 *
 * A randomisation list has to be reproducible. An ethics committee, a supervisor or a
 * journal may ask how it was made, and "the computer picked" is not an answer — so the
 * allocation tools draw from a deterministic generator driven by a seed the reader can
 * see, write down and type back in. The pipeline, stated exactly so it can be
 * re-implemented anywhere:
 *
 * 1. **Seed text → state.** cyrb128, the string hash bryc publishes alongside sfc32,
 *    turns the seed's UTF-16 code units into four 32-bit words.
 * 2. **State → stream.** Those words seed sfc32, Chris Doty-Humphrey's Small Fast Counter
 *    generator from the PractRand suite: 128 bits of state, one 32-bit output per call.
 * 3. **Stream → whole numbers.** A uniform integer below m takes the next output x and
 *    keeps it only when x < m × ⌊2³² / m⌋, returning x mod m. Rejecting the top sliver
 *    removes modulo bias: no value is ever favoured.
 * 4. **Whole numbers → shuffles.** Fisher–Yates (Durstenfeld's version): for i from the
 *    last position down to 1, swap position i with a uniform j in 0…i.
 *
 * Same seed and same inputs give the same list in any browser, on any device, next year.
 * That promise is why `research-design.test.ts` pins this generator's output: a refactor
 * that changed it would silently orphan every allocation anyone has recorded.
 *
 * Fresh seeds come from `crypto.getRandomValues`, and are only ever drawn inside event
 * handlers, never during render.
 */

/** A stream of uniformly distributed unsigned 32-bit integers. */
export type RandomSource = () => number;

const TWO_32 = 4_294_967_296;

// biome-ignore-start lint/suspicious/noBitwiseOperators: a hash and a PRNG are bit arithmetic by definition
/** cyrb128: four 32-bit words from a string — bryc's current version, final mix included. */
export function cyrb128(text: string): [number, number, number, number] {
  let h1 = 1_779_033_703;
  let h2 = 3_144_134_277;
  let h3 = 1_013_904_242;
  let h4 = 2_773_480_762;
  for (let i = 0; i < text.length; i++) {
    const k = text.charCodeAt(i);
    h1 = h2 ^ Math.imul(h1 ^ k, 597_399_067);
    h2 = h3 ^ Math.imul(h2 ^ k, 2_869_860_233);
    h3 = h4 ^ Math.imul(h3 ^ k, 951_274_213);
    h4 = h1 ^ Math.imul(h4 ^ k, 2_716_044_179);
  }
  h1 = Math.imul(h3 ^ (h1 >>> 18), 597_399_067);
  h2 = Math.imul(h4 ^ (h2 >>> 22), 2_869_860_233);
  h3 = Math.imul(h1 ^ (h3 >>> 17), 951_274_213);
  h4 = Math.imul(h2 ^ (h4 >>> 19), 2_716_044_179);
  h1 ^= h2 ^ h3 ^ h4;
  h2 ^= h1;
  h3 ^= h1;
  h4 ^= h1;
  return [h1 >>> 0, h2 >>> 0, h3 >>> 0, h4 >>> 0];
}

/** sfc32 seeded with four 32-bit words. */
export function sfc32(
  seed: readonly [number, number, number, number]
): RandomSource {
  let a = seed[0] | 0;
  let b = seed[1] | 0;
  let c = seed[2] | 0;
  let d = seed[3] | 0;
  return () => {
    const t = (((a + b) | 0) + d) | 0;
    d = (d + 1) | 0;
    a = b ^ (b >>> 9);
    b = (c + (c << 3)) | 0;
    c = (c << 21) | (c >>> 11);
    c = (c + t) | 0;
    return t >>> 0;
  };
}
// biome-ignore-end lint/suspicious/noBitwiseOperators: end of the hash and the PRNG

/** The documented generator for a seed: sfc32 seeded by cyrb128(seed). */
export function seededSource(seed: string): RandomSource {
  return sfc32(cyrb128(seed));
}

/** A uniform whole number in 0…bound − 1, without modulo bias. */
export function randomBelow(next: RandomSource, bound: number): number {
  if (!Number.isInteger(bound) || bound < 1 || bound > TWO_32) {
    throw new RangeError(
      `bound must be a whole number from 1 to 2^32: ${bound}`
    );
  }
  const limit = TWO_32 - (TWO_32 % bound);
  let x = next();
  while (x >= limit) {
    x = next();
  }
  return x % bound;
}

/** Fisher–Yates: a uniformly random permutation, as a new array. */
export function shuffle<T>(items: readonly T[], next: RandomSource): T[] {
  const out = [...items];
  for (let i = out.length - 1; i > 0; i--) {
    const j = randomBelow(next, i + 1);
    const held = out[i];
    out[i] = out[j];
    out[j] = held;
  }
  return out;
}

/**
 * A new seed: 64 random bits as four groups of hex digits ("7f3a-91c2-04be-6d18") —
 * short enough to copy into a protocol, long enough that two studies never collide.
 */
export function freshSeed(): string {
  const words = new Uint32Array(2);
  crypto.getRandomValues(words);
  const hex = [...words]
    .map((word) => word.toString(16).padStart(8, "0"))
    .join("");
  return [0, 4, 8, 12].map((start) => hex.slice(start, start + 4)).join("-");
}
